import { create, tsx } from '@dojo/framework/core/vdom';
import { ConnectStrip } from '../../widgets/ConnectStrip';
import { FAQ } from '../../widgets/FAQ';
import { Head } from '../../widgets/Head';
import { Hero } from '../../widgets/Hero';
import { IndustryExpertise } from '../../widgets/IndustryExpertise';
import { Summary } from '../../widgets/Summary';
import SummaryList from '../../widgets/SummaryList';
import ContentWrapper from '../../widgets/ContentWrapper';
import Section from '../../widgets/Section';
import Image from '../../widgets/Image';
import ColumnSummary from '../../widgets/ColumnSummary';
import SitePenLogo from '../../widgets/SitepenLogo';

const engineering = require('../../assets/images/illustration/engineering.svg');
const acceleratedImg = require('../../assets/images/icon-illustration/accelerated-results.svg');
const appiumImg = require('../../assets/images/logo/small/appium.svg');
const cypressImg = require('../../assets/images/logo/small/cypress.svg');
const jmeterImg = require('../../assets/images/logo/small/jmeter.svg');
const benefitsImg = require('../../assets/images/illustration/analysis.svg');
const consultingImg = require('../../assets/images/illustration/consulting.svg');
const coverageImg = require('../../assets/images/icon-illustration/enterprise-software-ux-design.svg');
const fasterImg = require('../../assets/images/icon-illustration/code-launch.svg');
const k6Img = require('../../assets/images/logo/small/k6.svg');
const reusabilityImg = require('../../assets/images/icon-illustration/back-end-services.svg');
const seleniumImg = require('../../assets/images/logo/small/selenium.svg');
const teamEfficiencyImg = require('../../assets/images/illustration/team-efficiency.svg');

const factory = create();

export const AutomatedTesting = factory(function AutomatedTesting() {
	return (
		<div>
			<Head
				title="Automated Software Testing | SitePen"
				description="Our QA automated testing company offers enterprise-level test automation services helping improve test coverage and release faster without sacrificing quality."
			/>

			<Hero background="level3Purple">
				{{
					xlTitle: (
						<div>
							Automated
							<br />
							Testing
						</div>
					)
				}}
			</Hero>

			<Section type="leading">
				<ContentWrapper>
					<SummaryList
						trailingBias="medium"
						listGuttering="none"
						style="alternating-reverse"
						itemPaddingBottom="md"
					>
						{{
							items: [
								{
									title: 'Ensure Quality With Automated Software Testing',
									blurb: (
										<virtual>
											<p>
												Creating a new software system is a substantial
												effort that requires extensive planning, expert
												execution, and meticulous testing. A successful
												launch doesn't mark the end of the system's journey,
												however, it is only the beginning. Successful
												systems experience evolution and improvement
												throughout their lives as they are adjusted and
												updated to accommodate their users' changing needs.
											</p>
											<p>
												Automated software testing has proven itself to be
												one of the most critical tools to support the stable
												and continuous evolution of a system. A thorough
												automated test suite codifies the system's business
												rules and serves to document the application's
												design. During the initial development, the test
												suite helps the engineering team add new features
												while maintaining existing functionality. After
												launch, the knowledge stored in the test suite
												becomes a critical resource for the maintenance
												teams to leverage, allowing them to evolve new
												features without compromising existing
												functionality.
											</p>
										</virtual>
									),
									image: <Image src={consultingImg} size="lg" />
								},
								{
									title: 'Why Develop with SitePen',
									blurb: (
										<virtual>
											<p>
												At SitePen, we believe that quality is the single,
												most important aspect of any project we undertake.
												Our commitment to delivering bug-free solutions
												infuses every part of our development activities.
											</p>
											<p>
												You will start to experience this as soon as you
												reach out to us. Our Sales and Project Management
												teams work with you to understand your goals and
												expectations, identifying the characteristics that
												have the most significant influence on the
												application's quality.
											</p>
											<p>
												Our engineering team will then develop the solution
												with these goals in mind. They also create extensive
												suites of automated tests that ensure that the code
												continues to work as intended. Finally, we
												continuously monitor the project in search of
												refactoring opportunities to improve the code's
												structure as it evolves. While not usually
												considered an essential aspect of quality assurance,
												we have found that superior code organization often
												leads to exceptional quality in the final product.
											</p>
										</virtual>
									),
									image: <SitePenLogo />
								},
								{
									blurb: (
										<virtual>
											<p>
												As the application approaches completion, we will
												shift the project to examine the quality of the
												solution. During this phase, we conduct tests that
												exercise all aspects of the complete solution. We
												verify the business logic's correctness, ensure the
												code is performant and will not bog down under
												real-world workloads, and harden the application
												against the vast array of security threats that
												today's applications face.
											</p>
											<p>
												Naturally, the final decision concerning the quality
												of the application resides with you. We will work
												with your infrastructure team to deploy the solution
												to a staging environment that allows you to conduct
												your own quality assurance verification. When all
												findings have been identified, we will resolve them,
												ensuring that you receive the most reliable and
												accurate application possible.
											</p>
										</virtual>
									),
									image: <Image src={engineering} lazy size="lg" />
								}
							]
						}}
					</SummaryList>
				</ContentWrapper>
			</Section>
			<Section shaded>
				<ContentWrapper>
					<ColumnSummary columnCount={2}>
						{{
							title: 'Benefits of Automated Testing',
							blurb: (
								<virtual>
									No single testing strategy is perfect; a multi-dimensional
									approach combining various manual and automated testing
									techniques is needed to ensure that a system performs as
									required. Among all of these tools, however, we have found
									automated testing to be the most overlooked but ultimately
									valuable tool available to today's development teams. An
									automated test suite codifies business rules to allow the
									system's workflows to be exercised quickly, efficiently, and
									repeatedly thus allowing regressions and bugs to be identified
									and corrected before they are deployed to production.
								</virtual>
							),
							items: [
								{
									title: 'Accelerated Results and Increased Accuracy',
									image: (
										<Image src={acceleratedImg} align="center" size="icon" />
									),
									blurb: (
										<virtual>
											Automated test suites document business rules and system
											architecture in a manner that allows system integrity to
											be quickly and easily confirmed. After they have been
											developed, automated tests are not subject to
											human-induced errors or delays. This characteristic
											makes the automated tests a speedy and reliable proof of
											the system's integrity - a vital attribute when making
											go/no-go decisions regarding production deployment.
										</virtual>
									)
								},
								{
									title: 'Increase in Test Coverage',
									image: <Image src={coverageImg} align="center" size="icon" />,
									blurb: (
										<virtual>
											At their most basic level, automated tests are programs
											that are designed to verify other programs. Since they
											are programs themselves, they can be instrumented to
											yield additional insights into the system-under-test.
											One such metric is test coverage. By instrumenting the
											system-under-test, the development team can quickly
											identify code that the test suite does not execute. This
											insight helps guide the development of future tests and
											highlights areas of potential weakness in the codebase.
										</virtual>
									)
								},
								{
									title: 'Reusability of Automated Tests',
									image: (
										<Image src={reusabilityImg} align="center" size="icon" />
									),
									blurb: (
										<virtual>
											When a system is primarily verified by manual testing,
											every test run requires a substantial amount of human
											interaction and is often time-intensive and error-prone.
											Automated tests only require human interaction during
											their initial development and verification. They can
											then be repeatedly executed, yielding remarkably
											consistent results.
										</virtual>
									)
								},
								{
									title: 'Accelerated Results and Increased Accuracy',
									image: <Image src={fasterImg} align="center" size="icon" />,
									blurb: (
										<virtual>
											A common concern with creating automated test suites is
											that the additional code required to write the tests
											will delay the project. Writing tests does indeed need
											more code to be written, but this is offset as the
											system's complexity grows beyond the engineering team's
											ability to easily verify that new features don't
											introduce unexpected regressions. Automated tests encode
											design decisions and rules as the system develops and
											quickly identify regressions before merging into the
											main development code.
										</virtual>
									)
								}
							]
						}}
					</ColumnSummary>
				</ContentWrapper>
			</Section>
			<Section>
				<ContentWrapper>
					<SummaryList>
						{{
							title: 'Automated Testing Tools',
							blurb: (
								<virtual>
									Automated test suites for today's web applications are powered
									by various high-quality tools and frameworks that simplify their
									creation, execution, and maintenance. While we pride ourselves
									on our ability to adapt to each customer's technology stack,
									there are a few tools that we find especially valuable.
								</virtual>
							),
							items: [
								{
									title: 'Selenium Automated Testing',
									blurb: (
										<p>
											When ThoughtWorks released the first version of what
											would become Selenium in 2004, it became one of the
											first tools designed to allow a program to simulate a
											user's interaction with a web browser. Today, it remains
											one of the primary tools used by development teams when
											creating end-to-end tests that simulate users executing
											real workflows.
										</p>
									),
									image: (
										<Image src={seleniumImg} lazy size="icon" align="center" />
									)
								},
								{
									title: 'Cypress',
									blurb: (
										<p>
											Cypress is a self-contained testing ecosystem,
											supporting the creation of unit, integration, and
											end-to-end tests. This diverse feature set allows every
											major type of automated test to be written using this
											single tool. Cypress offers a rich set of test-writing
											features and an extensive set of support tools that
											allow tests to be debugged and analyzed to ensure that
											they are performing as expected.
										</p>
									),
									image: (
										<Image src={cypressImg} lazy size="icon" align="center" />
									)
								},
								{
									title: 'JMeter',
									blurb: (
										<p>
											JMeter is an open-source project that is designed to
											load test web applications. It provides a test
											development model where test engineers can precisely
											control how the target application will be tested. Once
											the tests are developed, load tests can be automatically
											executed and analyzed via HTML-based load reports.
										</p>
									),
									image: <Image src={jmeterImg} lazy size="icon" align="center" />
								},
								{
									title: 'k6',
									blurb: (
										<p>
											k6 is another popular load testing tool for web services
											and web applications. It offers a complete set of
											capabilities for writing tests that ensure that the
											application performs well in various loading conditions,
											such as load testing to guarantee performance, stress
											tests to confirm system capacity, and soak tests to
											verify application stability over time.
										</p>
									),
									image: <Image src={k6Img} lazy size="icon" align="center" />
								},
								{
									title: 'Appium Testing',
									blurb: (
										<p>
											In many ways, Appium can be considered the mobile
											equivalent of Selenium. Whereas Selenium is designed to
											automate web browsers, Appium automates a test suite's
											execution on the vast array of available mobile devices.
											This capability makes it a vital tool when developing
											many of today's applications since users often insist on
											the ability to access systems using their personal or
											corporate-issued devices.
										</p>
									),
									image: <Image src={appiumImg} lazy size="icon" align="center" />
								}
							]
						}}
					</SummaryList>
				</ContentWrapper>
			</Section>
			<Section shaded>
				<IndustryExpertise />
			</Section>
			<Section>
				<ContentWrapper>
					<Summary reverse collapse="large">
						{{
							title: 'Choosing the Right Software Automated Testing Team',
							blurb: (
								<p>
									Many companies offer themselves up as testing experts. While
									some organizations might be able to succeed at being this
									specialized, we have found that the best testing teams have
									developed expertise in many different aspects of the software
									development process. The best automated testing teams naturally
									include testing experts, but they also need members that
									understand project management, software design, and application
									architecture. These skills inform the testing process by
									enabling the team to identify which tests to write and why they
									are essential. Additionally, the testing specialists in
									top-performing teams have a great deal of familiarity with the
									various testing tools available and develop the right mix of
									tools to maximize the effectiveness of the test suite while
									minimizing complexity.
								</p>
							),
							image: <Image src={teamEfficiencyImg} lazy size="lg" />
						}}
					</Summary>
				</ContentWrapper>
			</Section>
			<Section shaded>
				<ContentWrapper size="small">
					<Summary stack>
						{{
							image: <Image src={benefitsImg} lazy size="md" />,
							title: 'Benefits of Automated Testing Services',
							blurb: (
								<virtual>
									During the creation of an application, a vast amount of
									knowledge is gained by the development team. This knowledge is
									distilled and codified into the application's source code, but
									the nature of software leads to the business context being lost
									in the actual program code's details. Automated tests provide an
									"outsider's perspective" of what the application is supposed to
									do. While each test's implementation might be complicated, the
									tests' structure and naming often serve as a primary resource
									for maintenance teams as they evolve and refine the system to
									continue to meet the users' needs.
								</virtual>
							)
						}}
					</Summary>
				</ContentWrapper>
			</Section>
			<Section>
				<ConnectStrip />
			</Section>
			<Section type="trailing" shaded>
				<FAQ title="Automated Testing FAQs">
					{[
						{
							title: 'What Is Automated Testing?',
							content: (
								<p>
									In software development, automated testing is a quality control
									strategy that utilizes programs designed to confirm the proper
									behavior of other programs. Test automation programs are often
									written during the creation of another program or system to
									ensure that the target system meets correctness, performance,
									and resource utilization requirements.
								</p>
							)
						},
						{
							title: 'What Are the Advantages of Automated Testing?',
							content: (
								<p>
									Automated testing provides three main advantages. During the
									system's initial development, a properly constructed automated
									test suite ensures that existing features continue to behave as
									expected as new features are added. Automated testing also
									enables application developers to improve the application's
									structure by allowing them to extensively refactor and optimize
									the codebase while ensuring that regressions are not introduced.
									Finally, automated test suites are a valuable tool for
									maintenance teams to adapt the system to meet the users'
									changing needs without compromising the system's integrity.
								</p>
							)
						},
						{
							title: 'What Is the Difference Between Manual and Automated Testing?',
							content: (
								<virtual>
									<p>
										Manual testing involves human interaction at some level.
										Typically, one or more testers execute a series of test
										scripts that define the inputs that they should provide to
										the system and the expected results of those interactions.
										Involving people in the process is helpful since testers can
										identify suboptimal behavior that is not codified in the
										test scripts. However, manual testing tends to be
										error-prone and relatively slow since the testers'
										involvement makes them a part of the system-under-test.
									</p>
									<p>
										Automated testing requires human interaction as well, but
										their role is different. Instead of conducting the tests
										themselves, the engineers construct programs that exercise
										the target system to simulate some of the system's intended
										behavior. While this often requires more upfront work than
										manual testing, the resultant test programs can be executed
										repeatedly, consistently, and efficiently.
									</p>
								</virtual>
							)
						}
					]}
				</FAQ>
			</Section>
		</div>
	);
});

export default AutomatedTesting;
